import { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { apiGet, apiSend, apiUpload, ApiError } from "@/lib/api";
import { newAmapMap } from "@/lib/amap";
import { wgs84ToGcj02 } from "@/lib/geo";
import { confirmDialog, toast } from "@/components/ui/feedback";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

// 外业采集数据: sessions exported by the Android tracking app (apps/tracking)
// and imported through POST /tracking/import. Coordinates are raw WGS-84;
// the map is a bounds-fitted SVG plot, not a tile map.

interface TrackingSession {
  sessionId: string;
  startedAtEpochMs: number;
  endedAtEpochMs: number | null;
  status: string;
  sampleIntervalSeconds: number;
  appVersion: string;
  deviceModel: string;
}

interface SessionSummary extends TrackingSession {
  importedAt: string;
  trackPoints: number;
  manualPoints: number;
  networkEvents: number;
  gpsPoints: number;
}

interface TrackPoint {
  id: number;
  timestampEpochMs: number;
  latitude: number;
  longitude: number;
  accuracyM: number | null;
  altitudeM: number | null;
  speedMps: number | null;
  provider: string | null;
  isMock: boolean;
  wifiRssiDbm: number | null;
  signalStrengthDbm: number | null;
  cellularNetworkType: string | null;
  probeSuccess: boolean | null;
  probeLatencyMs: number | null;
  qualityLevel: string;
}

interface ManualPoint {
  id: number;
  trackPointId: number | null;
  timestampEpochMs: number;
  latitude: number | null;
  longitude: number | null;
  accuracyM: number | null;
  annotationStatus: string;
  name: string | null;
  category: string | null;
  remarks: string | null;
  sceneId?: string | null;
}

interface NetworkEvent {
  id: number;
  timestampEpochMs: number;
  connected: boolean;
  wifiRssiDbm: number | null;
  signalStrengthDbm: number | null;
  cellularNetworkType: string | null;
  probeSuccess: boolean | null;
  probeLatencyMs: number | null;
  qualityLevel: string;
}

interface SessionDetail {
  session: TrackingSession;
  trackPoints: TrackPoint[];
  manualPoints: ManualPoint[];
  networkEvents: NetworkEvent[];
}

// Quality levels emitted by the tracking app's TrackingService.
const qualityColors: Record<string, string> = {
  good_wifi: "#10b981",
  good_cellular: "#22c55e",
  good: "#34d399",
  limited: "#f59e0b",
  weak_wifi: "#f59e0b",
  weak_cellular: "#f59e0b",
  slow: "#f97316",
  poor: "#ef4444",
  offline: "#991b1b",
};
const qualityLabels: Record<string, string> = {
  good_wifi: "WiFi良好",
  good_cellular: "蜂窝良好",
  good: "良好",
  limited: "受限",
  weak_wifi: "WiFi弱",
  weak_cellular: "蜂窝弱",
  slow: "高延迟",
  poor: "探测失败",
  offline: "离线",
};
const qualityColor = (q: string) => qualityColors[q] ?? "#9ca3af";
const badQuality = new Set(["limited", "weak_wifi", "weak_cellular", "slow", "poor", "offline"]);

function fmtDateTime(ms: number): string {
  return new Date(ms).toLocaleString("zh-CN", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function fmtDuration(start: number, end: number | null): string {
  if (!end || end <= start) return "-";
  const s = Math.round((end - start) / 1000);
  if (s < 60) return `${s}秒`;
  if (s < 3600) return `${Math.floor(s / 60)}分${s % 60}秒`;
  return `${Math.floor(s / 3600)}小时${Math.floor((s % 3600) / 60)}分`;
}


function TrackingPage() {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sceneModal, setSceneModal] = useState<ManualPoint | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadSessions = useCallback(async () => {
    try {
      const list = await apiGet<SessionSummary[]>("/tracking/sessions");
      setSessions(list);
      return list;
    } catch {
      toast("加载采集会话失败", "error");
      return [];
    }
  }, []);

  useEffect(() => {
    loadSessions().then((list) => {
      if (list.length > 0) setSelectedId((cur) => cur ?? list[0].sessionId);
    });
  }, [loadSessions]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoadingDetail(true);
    apiGet<SessionDetail>(`/tracking/sessions/${selectedId}`)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch(() => { if (!cancelled) toast("加载会话详情失败", "error"); })
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    return () => { cancelled = true; };
  }, [selectedId]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const res = await apiUpload<{ imported: { sessionId: string; trackPoints: number; manualPoints: number }[] }>(
        "/tracking/import", file,
      );
      const n = res.imported.length;
      const pts = res.imported.reduce((s, x) => s + x.trackPoints, 0);
      toast(`导入成功:${n} 个会话,${pts} 个轨迹点`, "success");
      await loadSessions();
      if (res.imported[0]) setSelectedId(res.imported[0].sessionId);
    } catch (e) {
      toast(e instanceof ApiError ? `导入失败:${e.message}` : "导入失败", "error");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (sessionId: string) => {
    const ok = await confirmDialog({
      title: "删除采集会话",
      message: "轨迹点、标注点和网络事件将一并删除,且不可恢复。",
      confirmText: "删除",
      danger: true,
    });
    if (!ok) return;
    try {
      await apiSend("DELETE", `/tracking/sessions/${sessionId}`);
      toast("已删除", "success");
      if (selectedId === sessionId) setSelectedId(null);
      const list = await loadSessions();
      if (list.length > 0) setSelectedId((cur) => cur ?? list[0].sessionId);
    } catch {
      toast("删除失败", "error");
    }
  };

  const handleCreateScene = async (mp: ManualPoint, name: string, triggerRadius: number, description: string) => {
    if (!selectedId) return;
    try {
      const res = await apiSend<{ sceneId: string }>(
        "POST",
        `/tracking/sessions/${selectedId}/manual-points/${mp.id}/scene`,
        { name, triggerRadius, description },
      );
      setDetail((d) => d && {
        ...d,
        manualPoints: d.manualPoints.map((m) => (m.id === mp.id ? { ...m, sceneId: res.sceneId } : m)),
      });
      setSceneModal(null);
      toast(`已创建景点「${name}」`, "success");
    } catch (e) {
      toast(e instanceof ApiError ? `创建失败:${e.message}` : "创建失败", "error");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">外业采集数据</h1>
          <p className="text-sm text-gray-500 mt-1">现场 GPS 轨迹、标注点位与网络质量(采集 App 导出 JSON)</p>
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUpload(f);
            }}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            <i className={uploading ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"}></i>
            {uploading ? "导入中…" : "导入采集文件"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Session list */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">采集会话</h3>
            <span className="text-xs text-gray-400">{sessions.length} 个</span>
          </div>
          {sessions.length === 0 ? (
            <div className="p-10 text-center text-gray-400 text-sm">
              <i className="ri-route-line text-3xl block mb-2"></i>
              暂无数据,点击右上角导入采集 App 导出的 JSON 文件
            </div>
          ) : (
            <div className="divide-y divide-gray-100 max-h-[560px] overflow-y-auto">
              {sessions.map((s) => {
                const active = s.sessionId === selectedId;
                const gpsRatio = s.trackPoints > 0 ? Math.round((s.gpsPoints / s.trackPoints) * 100) : 0;
                return (
                  <button
                    key={s.sessionId}
                    onClick={() => setSelectedId(s.sessionId)}
                    className={`w-full text-left px-5 py-3.5 transition-colors ${
                      active ? "bg-teal-50" : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900">
                        {fmtDateTime(s.startedAtEpochMs)}
                      </span>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(s.sessionId);
                        }}
                        className="text-gray-300 hover:text-red-500 cursor-pointer p-1"
                        title="删除会话"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                      <span>{s.deviceModel}</span>
                      <span>·</span>
                      <span>时长 {fmtDuration(s.startedAtEpochMs, s.endedAtEpochMs)}</span>
                      <span>·</span>
                      <span>{s.sampleIntervalSeconds}s 采样</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                        轨迹 {s.trackPoints}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">
                        标注 {s.manualPoints}
                      </span>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded ${
                          gpsRatio >= 50 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"
                        }`}
                        title="provider=gps 的轨迹点占比(低说明大多是网络定位,精度差)"
                      >
                        GPS {gpsRatio}%
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-3 space-y-4">
          {loadingDetail ? (
            <div className="bg-white rounded-lg border border-gray-200 p-16 text-center text-gray-400 text-sm">
              <i className="ri-loader-4-line animate-spin text-2xl block mb-2"></i>
              加载中…
            </div>
          ) : !detail ? (
            <div className="bg-white rounded-lg border border-gray-200 p-16 text-center text-gray-400 text-sm">
              选择左侧会话查看轨迹详情
            </div>
          ) : (
            <SessionDetailView detail={detail} onCreateScene={(mp) => setSceneModal(mp)} />
          )}
        </div>
      </div>

      {/* Create-scene modal */}
      {sceneModal && (
        <CreateSceneModal
          point={sceneModal}
          onCancel={() => setSceneModal(null)}
          onSubmit={(name, radius, desc) => handleCreateScene(sceneModal, name, radius, desc)}
        />
      )}
    </div>
  );
}

function SessionDetailView({
  detail,
  onCreateScene,
}: {
  detail: SessionDetail;
  onCreateScene: (mp: ManualPoint) => void;
}) {
  const { trackPoints, manualPoints, networkEvents } = detail;
  const [selectedMp, setSelectedMp] = useState<number | null>(null);

  // Reset selection when switching sessions.
  useEffect(() => {
    setSelectedMp(null);
  }, [detail.session.sessionId]);

  const gpsCount = trackPoints.filter((p) => p.provider === "gps").length;
  const accs = trackPoints.map((p) => p.accuracyM).filter((a): a is number => a != null);
  const avgAcc = accs.length ? Math.round(accs.reduce((s, a) => s + a, 0) / accs.length) : null;
  const badCount = trackPoints.filter((p) => badQuality.has(p.qualityLevel)).length;

  const qualityDist = trackPoints.reduce<Record<string, number>>((acc, p) => {
    acc[p.qualityLevel] = (acc[p.qualityLevel] ?? 0) + 1;
    return acc;
  }, {});

  const chartData = networkEvents
    .filter((n) => n.probeLatencyMs != null || n.wifiRssiDbm != null || n.signalStrengthDbm != null)
    .map((n) => ({
      time: fmtTime(n.timestampEpochMs),
      延迟ms: n.probeLatencyMs,
      信号dBm: n.wifiRssiDbm ?? n.signalStrengthDbm,
    }));

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-xs text-gray-500 mb-1">轨迹点</div>
          <div className="text-xl font-bold text-gray-900">{trackPoints.length}</div>
          <div className="text-xs text-gray-400 mt-0.5">GPS {gpsCount} / 网络 {trackPoints.length - gpsCount}</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-xs text-gray-500 mb-1">平均定位精度</div>
          <div className="text-xl font-bold text-gray-900">{avgAcc != null ? `${avgAcc}m` : "-"}</div>
          <div className="text-xs text-gray-400 mt-0.5">越小越好</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-xs text-gray-500 mb-1">标注点位</div>
          <div className="text-xl font-bold text-amber-600">{manualPoints.length}</div>
          <div className="text-xs text-gray-400 mt-0.5">可转为景点</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-xs text-gray-500 mb-1">网络异常采样</div>
          <div className={`text-xl font-bold ${badCount > 0 ? "text-red-500" : "text-emerald-600"}`}>
            {trackPoints.length ? Math.round((badCount / trackPoints.length) * 100) : 0}%
          </div>
          <div className="text-xs text-gray-400 mt-0.5">{badCount} / {trackPoints.length} 点</div>
        </div>
      </div>

      {/* Map */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-semibold text-gray-900">
            轨迹与标注
            <span className="ml-2 text-xs font-normal text-gray-400" title="数据存储为 WGS-84 原始坐标,展示时转换为高德 GCJ-02">
              高德底图 · 存储 WGS-84
            </span>
          </h3>
          <div className="flex items-center gap-3 flex-wrap">
            {Object.entries(qualityDist).map(([q, n]) => (
              <span key={q} className="flex items-center gap-1 text-[11px] text-gray-500">
                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: qualityColor(q) }}></span>
                {qualityLabels[q] ?? q} {n}
              </span>
            ))}
          </div>
        </div>
        <TrackMap
          trackPoints={trackPoints}
          manualPoints={manualPoints}
          selectedMpId={selectedMp}
          onSelectMp={setSelectedMp}
        />
      </div>

      {/* Manual points */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">标注点位</h3>
        </div>
        {manualPoints.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">本次采集没有标注点</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                  <th className="px-5 py-2.5 font-medium">名称</th>
                  <th className="px-3 py-2.5 font-medium">分类</th>
                  <th className="px-3 py-2.5 font-medium">坐标</th>
                  <th className="px-3 py-2.5 font-medium">精度</th>
                  <th className="px-3 py-2.5 font-medium">时间</th>
                  <th className="px-3 py-2.5 font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {manualPoints.map((m) => (
                  <tr
                    key={m.id}
                    onClick={() => setSelectedMp(m.id)}
                    className={`cursor-pointer ${selectedMp === m.id ? "bg-teal-50" : "hover:bg-gray-50"}`}
                  >
                    <td className="px-5 py-2.5 font-medium text-gray-900">{m.name ?? "(未命名)"}</td>
                    <td className="px-3 py-2.5 text-gray-500">{m.category ?? "-"}</td>
                    <td className="px-3 py-2.5 text-gray-500 font-mono text-xs">
                      {m.latitude != null && m.longitude != null
                        ? `${m.latitude.toFixed(6)}, ${m.longitude.toFixed(6)}`
                        : "-"}
                    </td>
                    <td className="px-3 py-2.5 text-gray-500">{m.accuracyM != null ? `${Math.round(m.accuracyM)}m` : "-"}</td>
                    <td className="px-3 py-2.5 text-gray-500 text-xs">{fmtTime(m.timestampEpochMs)}</td>
                    <td className="px-3 py-2.5">
                      {m.sceneId ? (
                        <Link
                          to={`/knowledge/scenes/${m.sceneId}`}
                          className="text-xs text-emerald-600 hover:text-emerald-800 hover:underline flex items-center gap-1"
                          title="打开景点详情"
                        >
                          <i className="ri-check-line"></i>已创建景点
                          <i className="ri-external-link-line"></i>
                        </Link>
                      ) : m.latitude == null ? (
                        <span className="text-xs text-gray-300">无坐标</span>
                      ) : (
                        <button
                          onClick={() => onCreateScene(m)}
                          className="text-xs px-2.5 py-1 rounded bg-teal-50 text-teal-600 hover:bg-teal-100 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-map-pin-add-line mr-1"></i>创建为景点
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Network quality timeline */}
      {chartData.length > 1 && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">网络质量时间线(探测延迟 / 信号强度)</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#6b7280" }} />
                <YAxis yAxisId="lat" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#6b7280" }} />
                <YAxis yAxisId="sig" orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#6b7280" }} />
                <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb", fontSize: "12px" }} />
                <Legend />
                <Line yAxisId="lat" type="monotone" dataKey="延迟ms" stroke="#0d9488" strokeWidth={2} dot={false} connectNulls />
                <Line yAxisId="sig" type="monotone" dataKey="信号dBm" stroke="#8b5cf6" strokeWidth={2} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </>
  );
}

function TrackMap({
  trackPoints,
  manualPoints,
  selectedMpId,
  onSelectMp,
}: {
  trackPoints: TrackPoint[];
  manualPoints: ManualPoint[];
  selectedMpId: number | null;
  onSelectMp: (id: number) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const overlayRef = useRef<L.LayerGroup | null>(null);
  const mpMarkersRef = useRef<Map<number, L.CircleMarker>>(new Map());
  const onSelectMpRef = useRef(onSelectMp);
  onSelectMpRef.current = onSelectMp;

  // Create the map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = newAmapMap(containerRef.current);
    map.setView([30.82, 111.0], 10); // 两坝一峡 default until data arrives
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // (Re)draw track + manual points whenever the session data changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    overlayRef.current?.remove();
    const g = L.layerGroup().addTo(map);
    overlayRef.current = g;
    mpMarkersRef.current.clear();

    // 过滤无效坐标点：室内/未锁 GPS 的采样 lat/lng 是 null 或 0,0,若不剔除会被
    // 画在非洲外海 (0,0) 并拉一条直线到真实轨迹，fitBounds 也被撑到全球。
    const validTrack = trackPoints.filter(
      (p) => p.latitude != null && p.longitude != null && !(p.latitude === 0 && p.longitude === 0),
    );
    const trackLatLngs = validTrack.map((p) => wgs84ToGcj02(p.latitude, p.longitude));
    if (trackLatLngs.length > 1) {
      L.polyline(trackLatLngs, { color: "#0d9488", weight: 3, opacity: 0.55 }).addTo(g);
    }
    validTrack.forEach((p, i) => {
      L.circleMarker(trackLatLngs[i], {
        radius: 4.5,
        color: "#ffffff",
        weight: 1,
        fillColor: qualityColor(p.qualityLevel),
        fillOpacity: 0.9,
      })
        .bindTooltip(
          `${fmtTime(p.timestampEpochMs)} · ${p.provider ?? "?"} 定位 ±${p.accuracyM != null ? Math.round(p.accuracyM) : "?"}m<br/>` +
            `${p.latitude.toFixed(6)}, ${p.longitude.toFixed(6)}<br/>` +
            `网络: ${qualityLabels[p.qualityLevel] ?? p.qualityLevel}` +
            (p.probeLatencyMs != null ? ` / 延迟 ${p.probeLatencyMs}ms` : ""),
        )
        .addTo(g);
    });

    // Start / end badges, pushed to opposite sides so they stay readable
    // even when the whole track sits on one spot.
    if (trackLatLngs.length > 0) {
      const badge = (text: string, color: string, anchor: [number, number]) =>
        L.divIcon({
          className: "",
          html: `<div style="background:${color};color:#fff;font-size:11px;font-weight:600;line-height:1;padding:3px 7px;border-radius:9999px;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,.35)">${text}</div>`,
          iconAnchor: anchor,
        });
      L.marker(trackLatLngs[0], { icon: badge("起", "#0d9488", [38, 9]), interactive: false }).addTo(g);
      L.marker(trackLatLngs[trackLatLngs.length - 1], { icon: badge("终", "#6366f1", [-12, 9]), interactive: false }).addTo(g);
    }

    // Manual labeled points: accuracy circle (real meters) + marker with a
    // permanent name label. Same-coordinate labels stack upward instead of
    // overlapping.
    const clusterIdx = new Map<string, number>();
    const manualLatLngs: [number, number][] = [];
    manualPoints
      .filter((m) => m.latitude != null && m.longitude != null)
      .forEach((m) => {
        const [lat, lng] = wgs84ToGcj02(m.latitude as number, m.longitude as number);
        manualLatLngs.push([lat, lng]);
        if (m.accuracyM != null && m.accuracyM > 0) {
          L.circle([lat, lng], {
            radius: m.accuracyM,
            color: "#f59e0b",
            weight: 1,
            opacity: 0.5,
            fillColor: "#f59e0b",
            fillOpacity: 0.07,
            dashArray: "4 4",
            interactive: false,
          }).addTo(g);
        }
        const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
        const idx = clusterIdx.get(key) ?? 0;
        clusterIdx.set(key, idx + 1);
        const mk = L.circleMarker([lat, lng], {
          radius: 8,
          color: "#ffffff",
          weight: 2,
          fillColor: "#f59e0b",
          fillOpacity: 1,
        })
          .bindTooltip(`${m.name ?? `#${m.id}`} <span style="color:#9ca3af">±${m.accuracyM != null ? Math.round(m.accuracyM) : "?"}m</span>`, {
            permanent: true,
            direction: "top",
            offset: [0, -(12 + idx * 26)],
            className: "tracking-mp-label",
          })
          .on("click", () => onSelectMpRef.current(m.id))
          .addTo(g);
        mpMarkersRef.current.set(m.id, mk);
      });

    const all = [...trackLatLngs, ...manualLatLngs];
    if (all.length > 0) {
      map.fitBounds(L.latLngBounds(all), { padding: [48, 48], maxZoom: 17 });
    }
  }, [trackPoints, manualPoints]);

  // Highlight the table-selected manual point.
  useEffect(() => {
    mpMarkersRef.current.forEach((mk, id) => {
      const selected = id === selectedMpId;
      mk.setStyle({ fillColor: selected ? "#0d9488" : "#f59e0b" });
      mk.setRadius(selected ? 10 : 8);
    });
    if (selectedMpId != null) {
      const mk = mpMarkersRef.current.get(selectedMpId);
      if (mk) mapRef.current?.panTo(mk.getLatLng());
    }
  }, [selectedMpId]);

  return (
    <>
      <style>{`
        .tracking-mp-label {
          font-size: 12px; font-weight: 600; color: #92400e;
          background: rgba(255,255,255,.95); border: 1px solid #fcd34d;
          border-radius: 6px; padding: 2px 7px;
          box-shadow: 0 1px 3px rgba(0,0,0,.15);
        }
        .tracking-mp-label::before { border-top-color: #fcd34d; }
      `}</style>
      <div ref={containerRef} className="h-[440px] relative z-0" />
    </>
  );
}

function CreateSceneModal({
  point,
  onCancel,
  onSubmit,
}: {
  point: ManualPoint;
  onCancel: () => void;
  onSubmit: (name: string, triggerRadius: number, description: string) => void;
}) {
  const [name, setName] = useState(point.name ?? "");
  const [radius, setRadius] = useState(300);
  const [desc, setDesc] = useState(point.remarks ?? "");
  const [submitting, setSubmitting] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-900 mb-1">从标注点创建景点</h3>
        <p className="text-xs text-gray-500 mb-4 font-mono">
          {point.latitude?.toFixed(6)}, {point.longitude?.toFixed(6)}(实测 WGS-84,±
          {point.accuracyM != null ? Math.round(point.accuracyM) : "?"}m)
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">景点名称</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-400"
              placeholder="如:三峡大坝观景台"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">触发半径(米)</label>
            <input
              type="number"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">描述(可选)</label>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 resize-none"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            取消
          </button>
          <button
            disabled={!name.trim() || submitting}
            onClick={() => {
              setSubmitting(true);
              onSubmit(name.trim(), radius, desc.trim());
            }}
            className="px-4 py-2 text-sm bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50"
          >
            创建景点
          </button>
        </div>
      </div>
    </div>
  );
}

export default TrackingPage;
